
/**
 * @author Jasper Howard <jhoward@peppermillcas.com>
 * @copyright Copyright (C) 2023, Peppermill Inc. All rights reserved. Redistribution and use in source and binary forms, with or without modification, are not permitted.
 * @version 1.0.5
 * @changelog
 *  2023-01-18 (1.0.0) (jhoward)
 *    - <show-component> matches the output and functionality of server-side rendered show wrapper.
 *
 *  2023-01-24 (1.0.1) (jhoward)
 *    - <show-component> now allows server to dictate timezone of show.show_date and show.show_goes_on_sale.
 *
 *  2023-02-23 (1.0.2) (jhoward)
 *    - Fix typo in show_canceled property of the show object in <show-component>.
 *
 *  2023-03-28 (1.0.3) (jhoward)
 *    - <show-component> now shows the date of the week for show date.
 *
 *  2023-05-15 (1.0.4) (jhoward)
 *    - <show-component> now shows the time of the show in MT.
 *
 *  2023-05-15 (1.0.5) (jhoward)
 *    - <show-component> now shows "sold out" in red and bold.
 *    - <show-component> now considers the show.show_sold_out value when determining if the show is available to book..
 */

import {defineComponent, PropType} from "vue";
import {Show} from "../defs";
import ShowButtonOnSaleCountdown from "./ShowButtonOnSaleCountdown.vue";

export default defineComponent({
  name: "Show",
  components: {ShowButtonOnSaleCountdown},
  props: {
    show: {
      type: Object as PropType<Show>,
      required: true
    },
    packages: {
      type: Array as PropType<string[]>,
      default: []
    }
  },
  data() {
    return {
      language: {
        en: {
          days: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
          daysShort: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
          daysMin: ['S', 'M', 'T', 'W', 'T', 'F', 'S'],
          months: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
          monthsShort: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
          today: 'Today',
          clear: 'Clear',
          dateFormat: 'mm/dd/yyyy',
          timeFormat: 'hh:ii aa',
          firstDay: 0
        }
      },

      renderOnSaleCounter: 0,
    };
  },
  computed: {
    containerClasses() {
      return {
        'show--container-booked': !!this.show.is_package_booked,
        'show--container-package_available': !!this.show.is_package_available,
      };
    },
    imageStyles() {
      return {
        'background-image': 'url(\'' + this.imageUrlPath + '\')',
        'background-position': this.show.show_poster_focus_x + '% ' + this.show.show_poster_focus_y + '%',
      };
    },
    showUrlPath(): string {
      return '/shows/' + this.show.show_title.replace(/\s/g, '-').toLowerCase() + '/';
    },
    imageUrlPath(): string | null {
      if (this.show.image && this.show.image.image_type) {
        return this.show.image.image_type.it_directory + this.show.image.image_filename;
      }
      return null;
    },
    isAvailableToBook(): boolean {
      this.renderOnSaleCounter;
      return (!this.onSaleDate || (new Date()).getTime() >= this.onSaleDate) && this.show.show_sold_out !== 1;
    },
    isAvailableToBookInFuture(): boolean {
      this.renderOnSaleCounter;
      return (this.onSaleDate && this.onSaleDate.getTime() > (new Date()).getTime());
    },
    isPackageAvailable(): boolean {
      return -1 !== this.packages.indexOf(this.packageName)
    },
    packageName() {
      let name = this.show.show_title.toLowerCase();
      if (-1 !== name.indexOf('(')) {
        name = name.split('(')[0];
      }

      return name;
    },
    onSaleDate(): Date | null {
      if (this.show.show_goes_on_sale_date !== '0000-00-00 00:00:00') {
        return new Date(this.show.show_goes_on_sale_date);
      }

      return null;
    },
    showDate(): Date | null {
      if (this.show.show_date !== '0000-00-00 00:00:00') {
        return new Date(this.show.show_date);
      }

      return null;
    },
    showDateString(): string {
      if (this.showDate) {
        return this.language.en.days[this.showDate.getDay()] + ', ' + this.language.en.months[this.showDate.getMonth()] + ' '
            + this.showDate.getDate() + ', '
            + this.showDate.getFullYear()
      }

      return '';
    },
    showTime(): string {
      if (this.showDate) {
        const timeString = this.showDate.toLocaleTimeString([], {timeZone: 'America/Denver'});
        const matches = /^(\d+):(\d+)(:\d+)? ([AP]M)/.exec(timeString);
        if (matches) {
          return parseInt(matches[1], 10) + ':' + matches[2] + ' ' + matches[4] + ' MT';
        } else {
          return timeString;
        }
      }

      return '';
    },
    bookingLink(): string {
      if ('TIX' === this.show.show_ticket_provider) {
        return 'https://www.tix.com/ticket-sales/wendover/7061/event/' + encodeURIComponent(this.show.show_showgate_code);
      }

      return 'https://reserve1.peppermillcas.com/cgi-bin/lansaweb?PROCFUN+NETSLS+NETSLS+TKW+funcparms+IOFRWW(A010):' + encodeURIComponent(this.show.show_showgate_code);
    },
  },
  methods: {
    handleBookingLinkClick() {
      if (this.showDate) {
        window.location = '/shows/terms-and-conditions/?c=' + encodeURIComponent(this.show.show_showgate_code) + '&m=' + encodeURIComponent(this.showDate.getMonth() + 1) + '&d=' + encodeURIComponent(this.showDate.getDate()) + '&y=' + encodeURIComponent(this.showDate.getFullYear());
      }
    },

    updateOnSale() {
      this.renderOnSaleCounter++;
    },
  }
})
